import {Text} from '@dropbox/dig-components/typography';
import cx from 'classnames';
import {t} from 'i18next';
import {HealthScoreIcon} from 'views/workstreams/WorkstreamHealthScore';

import styles from './WorkstreamAuditCompanyReadinessHeader.module.css';
import {
  WORKSTREAM_AUDIT_COMPANY_READINESS_ALL_FILTER,
  WORKSTREAM_AUDIT_COMPANY_READINESS_ATTENTION_FILTER,
  WORKSTREAM_AUDIT_COMPANY_READINESS_COMPLETE_FILTER,
  WORKSTREAM_AUDIT_COMPANY_READINESS_CRITICAL_FILTER,
  type WorkstreamAuditCompanyReadinessCounts,
  type WorkstreamAuditCompanyReadinessFilter,
} from './workstreamAuditUtils';

export const WORKSTREAM_AUDIT_COMPANY_READINESS_HEADER_TEST_ID =
  'workstream-audit-company-readiness-header';
export const getWorkstreamAuditCompanyReadinessButtonTestId = (
  filter: WorkstreamAuditCompanyReadinessFilter
) => `workstream-audit-company-readiness-button-${filter}`;
export const getWorkstreamAuditCompanyReadinessValueTestId = (
  filter: WorkstreamAuditCompanyReadinessFilter
) => `workstream-audit-company-readiness-value-${filter}`;
export const getWorkstreamAuditCompanyReadinessCountTestId = (
  filter: WorkstreamAuditCompanyReadinessFilter
) => `workstream-audit-company-readiness-count-${filter}`;

const formatPercentage = (count: number, totalCount: number) =>
  totalCount > 0 ? Math.round((count / totalCount) * 100) : 0;

export const WorkstreamAuditCompanyReadinessHeader = ({
  activeFilter,
  counts,
  onFilterChange,
  totalCount,
  hideTopBorder = false,
}: {
  activeFilter: WorkstreamAuditCompanyReadinessFilter;
  counts: WorkstreamAuditCompanyReadinessCounts;
  onFilterChange: (filter: WorkstreamAuditCompanyReadinessFilter) => void;
  totalCount: number;
  hideTopBorder?: boolean;
}) => {
  const safeTotalCount = Math.max(0, totalCount);
  const metrics: Array<{
    count: number;
    filter: WorkstreamAuditCompanyReadinessFilter;
    label: string;
    percentage?: number;
    score?: 'poor' | 'adequate' | 'exemplary';
  }> = [
    {
      filter: WORKSTREAM_AUDIT_COMPANY_READINESS_ALL_FILTER,
      label: t('workstream_audit_company_readiness_all_label').toString(),
      count: safeTotalCount,
    },
    {
      filter: WORKSTREAM_AUDIT_COMPANY_READINESS_COMPLETE_FILTER,
      label: t('workstream_audit_company_readiness_complete_label').toString(),
      count: counts.complete,
      percentage: formatPercentage(counts.complete, safeTotalCount),
      score: 'exemplary',
    },
    {
      filter: WORKSTREAM_AUDIT_COMPANY_READINESS_ATTENTION_FILTER,
      label: t('workstream_audit_company_readiness_attention_label').toString(),
      count: counts.attention,
      percentage: formatPercentage(counts.attention, safeTotalCount),
      score: 'adequate',
    },
    {
      filter: WORKSTREAM_AUDIT_COMPANY_READINESS_CRITICAL_FILTER,
      label: t('workstream_audit_company_readiness_critical_label').toString(),
      count: counts.critical,
      percentage: formatPercentage(counts.critical, safeTotalCount),
      score: 'poor',
    },
  ];

  return (
    <div
      className={cx(styles.companyReadinessHeader, {
        [styles.companyReadinessHeaderFlush]: hideTopBorder,
      })}
      data-testid={WORKSTREAM_AUDIT_COMPANY_READINESS_HEADER_TEST_ID}
      role="group"
      aria-label={t('workstream_audit_company_readiness_filters').toString()}
    >
      <div className={styles.companyReadinessGrid}>
        {metrics.map((metric) => (
          <button
            key={metric.filter}
            type="button"
            className={cx(styles.companyReadinessCell, {
              [styles.companyReadinessCellActive]: activeFilter === metric.filter,
            })}
            data-testid={getWorkstreamAuditCompanyReadinessButtonTestId(metric.filter)}
            aria-pressed={activeFilter === metric.filter}
            onClick={() => onFilterChange(metric.filter)}
          >
            <Text size="small" className={styles.companyReadinessLabel}>
              {metric.label}
            </Text>
            <span
              className={styles.companyReadinessValue}
              data-testid={getWorkstreamAuditCompanyReadinessValueTestId(metric.filter)}
            >
              {metric.score ? <HealthScoreIcon score={metric.score} iconSize={20} gap={0} /> : null}
              <span
                className={styles.companyReadinessCount}
                data-testid={getWorkstreamAuditCompanyReadinessCountTestId(metric.filter)}
              >
                {metric.count}
              </span>
              {metric.percentage !== undefined ? (
                <span className={styles.companyReadinessPercentage}>
                  {t('workstream_audit_company_readiness_percentage', {
                    percentage: metric.percentage,
                  }).toString()}
                </span>
              ) : null}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};
